import { useEffect, useMemo, useState } from "react"
import { motion } from "motion/react"

import { useRequest } from "../../hooks/useRequest"
import { appStore } from "../../store/appStore"
import { Movie } from "../../types/entities/movie"
import { POSTERS_BACKGROUND_MASK } from "./postersBackgroundMask"

type RowConfig = {
    maskPosition: string
    marginTop: number
    itemsEnd: boolean
    height: number
    direction: "left" | "right"
    duration: number
    offset: number
}

const ROWS: RowConfig[] = [
    {
        maskPosition: "24px -8px",
        marginTop: 0,
        itemsEnd: false,
        height: 189,
        direction: "left",
        duration: 70,
        offset: 0,
    },
    {
        maskPosition: "24px -230.795px",
        marginTop: 222.8,
        itemsEnd: true,
        height: 190,
        direction: "right",
        duration: 95,
        offset: 2,
    },
    {
        maskPosition: "24px -453.591px",
        marginTop: 445.59,
        itemsEnd: true,
        height: 189,
        direction: "left",
        duration: 82,
        offset: 4,
    },
]

const MIN_PER_ROW = 7
const POSTER_ASPECT = 473 / 677

/** min-height секции: карусель absolute, без этого футер наезжает на постеры */
export const POSTERS_SECTION_MIN_H = "min-h-[380px] sm:min-h-[500px] lg:min-h-[620px]"

function posterUrl(movie: Movie): string | null {
    return movie.poster?.big ?? movie.poster?.small ?? null
}

function buildRowImages(posters: string[], offset: number): string[] {
    return Array.from({ length: MIN_PER_ROW }, (_, i) => posters[(offset + i) % posters.length])
}

function PosterRow({
    images,
    maskPosition,
    marginTop,
    itemsEnd,
    height,
    direction,
    duration,
}: {
    images: string[]
    maskPosition: string
    marginTop: number
    itemsEnd: boolean
    height: number
    direction: "left" | "right"
    duration: number
}) {
    const track = [...images, ...images]

    return (
        <div
            className="col-1 row-1 overflow-hidden"
            style={{
                width: 940,
                marginTop,
                maskImage: `url("${POSTERS_BACKGROUND_MASK}")`,
                WebkitMaskImage: `url("${POSTERS_BACKGROUND_MASK}")`,
                maskSize: "940px 650px",
                WebkitMaskSize: "940px 650px",
                maskRepeat: "no-repeat",
                WebkitMaskRepeat: "no-repeat",
                maskPosition,
                WebkitMaskPosition: maskPosition,
            }}>
            <motion.div
                className="flex w-max gap-[14px] will-change-transform"
                style={{ alignItems: itemsEnd ? "flex-end" : "flex-start" }}
                animate={{ x: direction === "left" ? ["0%", "-50%"] : ["-50%", "0%"] }}
                transition={{ duration, ease: "linear", repeat: Infinity }}>
                {track.map((src, i) => {
                    const width = Math.round(height * POSTER_ASPECT)
                    return (
                        <div
                            key={`${src}-${i}`}
                            className="relative shrink-0 rounded-[7px]"
                            style={{ width, height, aspectRatio: "473/677" }}>
                            <img alt="" src={src} className="absolute inset-0 size-full max-w-none rounded-[7px] object-cover" />
                        </div>
                    )
                })}
            </motion.div>
        </div>
    )
}

async function fetchPosterMovies(
    requestData: ReturnType<typeof useRequest>["requestData"],
    cinemaId: number,
): Promise<Movie[]> {
    const datesRes = await requestData<string[]>({
        type: "GET",
        path: `cinema/${cinemaId}/film/dates`,
        disableError: true,
    })
    if (datesRes.status === "success" && datesRes.data?.length) {
        const now = new Date()
        const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`
        const date = datesRes.data.includes(today) ? today : datesRes.data[0]
        const films = await requestData<Movie[]>({
            type: "GET",
            path: `cinema/${cinemaId}/film?date=${date}`,
            disableError: true,
        })
        if (films.status === "success" && films.data?.length) {
            return films.data
        }
    }

    // soon не содержит poster — только запасной источник списка фильмов
    const soon = await requestData<Movie[]>({
        type: "GET",
        path: `cinema/${cinemaId}/film/soon`,
        disableError: true,
    })
    return soon.status === "success" && soon.data ? soon.data : []
}

type PostersBackgroundProps = {
    /** На мобильных: по центру (билеты) или справа (главная) */
    mobileAlign?: "right" | "center"
}

const POSITION_RIGHT =
    "left-auto right-[-120px] origin-top-right max-sm:scale-[0.55] sm:max-lg:scale-[0.78] sm:max-lg:right-[-40px] lg:right-0 xl:right-[max(0px,calc((100vw-1240px)/2))]"

const POSITION_CENTER_MOBILE =
    "max-sm:left-1/2 max-sm:right-auto max-sm:-translate-x-1/2 max-sm:origin-top max-sm:scale-[0.55] sm:left-auto sm:translate-x-0 sm:origin-top-right sm:scale-[0.78] sm:right-[-40px] lg:right-0 xl:right-[max(0px,calc((100vw-1240px)/2))]"

export default function PostersBackground({ mobileAlign = "right" }: PostersBackgroundProps) {
    const { cinema } = appStore()
    const { requestData } = useRequest()
    const [posters, setPosters] = useState<string[]>([])

    useEffect(() => {
        let cancelled = false

        const load = async () => {
            const movies = await fetchPosterMovies(requestData, cinema.id)
            if (cancelled) return

            const urls = [...new Set(movies.map(posterUrl).filter((url): url is string => Boolean(url)))]
            setPosters(urls)
        }

        load()
        return () => {
            cancelled = true
        }
        // requestData пересоздаётся на каждом рендере
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cinema.id])

    const rows = useMemo(() => {
        if (posters.length === 0) return []
        return ROWS.map(row => buildRowImages(posters, row.offset))
    }, [posters])

    if (rows.length === 0) return null

    const positionClasses = mobileAlign === "center" ? POSITION_CENTER_MOBILE : POSITION_RIGHT

    return (
        <div
            className={`pointer-events-none absolute -top-[40px] z-0 select-none opacity-100 ${positionClasses}`}
            aria-hidden>
            <div className="relative mt-[8px] inline-grid grid-cols-[max-content] grid-rows-[max-content] leading-[0]">
                {ROWS.map((row, index) => (
                    <PosterRow
                        key={index}
                        images={rows[index]}
                        maskPosition={row.maskPosition}
                        marginTop={row.marginTop}
                        itemsEnd={row.itemsEnd}
                        height={row.height}
                        direction={row.direction}
                        duration={row.duration}
                    />
                ))}
            </div>
        </div>
    )
}
